import { useState } from 'react'
import { apiErrorMessage } from '../../../features/locations/api-errors'
import { useLocation } from '../../../features/locations/use-location'
import {
  type BounceKind,
  type SwitchPort,
  useBouncePort,
  useRefreshSwitchPorts,
  useSwitchPorts,
} from '../../../features/locations/use-switch-ports'
import { useIdOrCode } from '../../use-id-or-code'
import { ActionRow, Btn, ConfirmDialog, Inline, PageHeader, th } from './controls'
import { mono, Note, Section, SkeletonRows, Status, serif } from './ui'

const portLabel = (s: SwitchPort['status']) => (s === 'connected' ? 'Up' : s === 'disabled' ? 'Down' : 'Idle')

interface Pending {
  port: SwitchPort
  kind: BounceKind
  prompt: string
}

export function LedgerSwitchPorts() {
  const idOrCode = useIdOrCode()
  const location = useLocation(idOrCode)
  const ports = useSwitchPorts(idOrCode)
  const bounce = useBouncePort()
  const refreshPorts = useRefreshSwitchPorts()

  const [confirm, setConfirm] = useState<Pending | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const header = (
    <PageHeader
      idOrCode={idOrCode}
      section="Network"
      page="Switch ports"
      title="Switch ports"
      standfirst="Every port on the store switch, what is plugged into it, and a way to give it a nudge."
    />
  )

  if (location.isLoading || ports.isLoading)
    return (
      <article>
        {header}
        <SkeletonRows />
      </article>
    )
  const storeCode = location.data?.aptosStoreCode ?? location.data?.code ?? null
  if (location.error || ports.error || !ports.data || !storeCode)
    return (
      <article>
        {header}
        <Note tone="error">
          The ports could not be read: {ports.error ? apiErrorMessage(ports.error) : 'store code not found.'}
        </Note>
      </article>
    )

  const list = ports.data.ports
  const up = list.filter((p) => p.status === 'connected').length
  const busy = bounce.isPending

  const start = (port: SwitchPort, kind: BounceKind) => {
    setNotice(null)
    bounce.mutate(
      { storeCode, portId: port.portId, kind },
      {
        onSuccess: (r) => {
          if (r.status === 'needs-confirmation') setConfirm({ port, kind, prompt: r.prompt })
          else setNotice(`${kind === 'poe' ? 'PoE on port' : 'Port'} ${port.portId} was bounced.`)
        },
      },
    )
  }

  const confirmed = () => {
    if (!confirm) return
    const { port, kind } = confirm
    bounce.mutate(
      { storeCode, portId: port.portId, kind, confirmed: true },
      {
        onSuccess: () => {
          setNotice(`${kind === 'poe' ? 'PoE on port' : 'Port'} ${port.portId} was bounced.`)
          setConfirm(null)
        },
      },
    )
  }

  return (
    <article>
      {header}
      <ActionRow>
        <Btn
          disabled={refreshing}
          onClick={async () => {
            setRefreshing(true)
            try {
              await refreshPorts()
            } finally {
              setRefreshing(false)
            }
          }}
        >
          {refreshing ? 'Refreshing…' : 'Refresh ports'}
        </Btn>
        <span className="text-[14px] italic text-[var(--color-muted)]" style={serif}>
          {up} of {list.length} up{ports.data.switchVendor ? `, on a ${ports.data.switchVendor} switch` : ''}.
        </span>
      </ActionRow>
      <div aria-live="polite">
        {bounce.error && !confirm ? (
          <Inline tone="bad">{apiErrorMessage(bounce.error)}</Inline>
        ) : notice ? (
          <Inline tone="ok">{notice}</Inline>
        ) : null}
      </div>

      <Section title="Ports">
        {list.length === 0 ? (
          <Note>No switch ports are reported for this store.</Note>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse" aria-label="Switch ports">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  {['Port', 'Status', 'VLAN', 'Connected client', 'In / out', ''].map((h) => (
                    <th key={h || 'actions'} scope="col" className={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.map((p) => (
                  <tr key={p.portId} className="h-11 border-b border-[var(--color-border)]">
                    <td className="pr-4">
                      <span className="text-[13px]" style={mono}>
                        {p.portId}
                      </span>
                      {p.name ? (
                        <span className="ml-2 text-[14px] italic text-[var(--color-muted)]" style={serif}>
                          {p.name}
                        </span>
                      ) : null}
                    </td>
                    <td className="pr-4">
                      <Status label={portLabel(p.status)} />
                    </td>
                    <td className="pr-4 text-[13px]" style={mono}>
                      {p.vlanTag ?? '—'}
                    </td>
                    <td className="pr-4 text-[14px]">
                      {p.clientMac ? (
                        <>
                          <span style={serif}>{p.clientDescription ?? p.clientIp ?? ''}</span>
                          <span className="ml-2 text-[12px] text-[var(--color-muted)]" style={mono}>
                            {p.clientMac}
                          </span>
                        </>
                      ) : (
                        <span className="text-[var(--color-muted)]">—</span>
                      )}
                    </td>
                    <td className="pr-4 text-[12px] text-[var(--color-muted)]" style={mono}>
                      {p.trafficReceived || p.trafficSent
                        ? `${p.trafficReceived ?? '—'} / ${p.trafficSent ?? '—'}`
                        : '—'}
                    </td>
                    <td className="text-right">
                      <span className="inline-flex gap-4">
                        <Btn disabled={busy || !p.enabled} onClick={() => start(p, 'port')}>
                          Bounce
                        </Btn>
                        <Btn disabled={busy || !p.enabled} onClick={() => start(p, 'poe')}>
                          PoE
                        </Btn>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <ConfirmDialog
        open={confirm !== null}
        danger
        title={`Bounce ${confirm?.kind === 'poe' ? 'PoE on ' : ''}port ${confirm?.port.portId ?? ''}?`}
        body={confirm?.prompt}
        confirmLabel="Yes, bounce it"
        pending={busy}
        error={bounce.error ? apiErrorMessage(bounce.error) : null}
        onConfirm={confirmed}
        onClose={() => {
          bounce.reset()
          setConfirm(null)
        }}
      />
    </article>
  )
}
